import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { SlidePanel } from "../SlidePanel";
import { Btn } from "../UI";
import { FLabel, FSelect, FTextarea } from "../../pages/engagement/FormControls";
import { DateField } from "../../pages/engagement/DateField";
import { resolveLibrary } from "../MedicationPicker";
import { RxLineFields } from "./RxLineFields";
import { PharmacyPicker } from "./PharmacyPicker";
import { fmtDate, todayIso } from "./rxShared";
import { Plus, PenLine, Save } from "lucide-react";
import { useSaveShortcut, SAVE_HINT } from "../../lib/useSaveShortcut";

const NEW_LINE = () => ({ id: Math.random().toString(36).slice(2, 10), medication: "", catalog_id: "", presentation_id: "", strength: "", form: "", route: "", frequency: "", directions: "",
  quantity: "", quantity_unit: "", refills: "0", days_supply: "", start_date: "", diagnosis_id: "", schedule: "", daw: false, prn: false, notes: "",
  pharmacy_id: "", max_daily_dose: "", label: false, med_ed: false, samples: false, samples_amount: "" });

const fromRx = (rx, meId) => rx
  ? { prescriber_id: rx.prescriber_id, appointment_id: rx.appointment_id || "", pharmacy_id: rx.pharmacy?.id || "", notes: rx.notes || "",
      written_date: rx.written_date || todayIso(), lines: rx.lines.map((l) => ({ ...NEW_LINE(), ...l })) }
  : { prescriber_id: meId, appointment_id: "", pharmacy_id: "", notes: "", written_date: todayIso(), lines: [NEW_LINE()] };

/** Create or edit a draft prescription. Only prescribers reach this panel. */
export function RxEditor({ open, onClose, client, rx, meta, me, onSaved }) {
  const [form, setForm] = useState(() => fromRx(rx, me.id));
  const [opts, setOpts] = useState({ visits: [], pharmacies: [], diagnoses: [] });
  const [busy, setBusy] = useState(false);
  const [prefFilled, setPrefFilled] = useState(false);
  useEffect(() => { if (open) { setForm(fromRx(rx, me.id)); setPrefFilled(false); } }, [open, rx, me.id]);
  useEffect(() => {
    if (!open) return;
    Promise.all([api.get(`/clients/${client.id}/prescription-visits`), api.get("/pharmacy-directory"), api.get(`/clients/${client.id}/diagnoses`), api.get(`/clients/${client.id}/pharmacies`)])
      .then(([v, p, d, cp]) => {
        setOpts({ visits: v.data, pharmacies: p.data, diagnoses: (d.data || []).filter((x) => x.status === "Active") });
        const pref = (cp.data || []).find((x) => x.preferred && x.directory_id && p.data.some((e) => e.id === x.directory_id));
        if (!rx && pref) { setForm((s) => (s.pharmacy_id ? s : { ...s, pharmacy_id: pref.directory_id })); setPrefFilled(true); }
      })
      .catch((e) => toast.error(apiError(e)));
  }, [open, client.id, rx]);
  const setLine = (i, patch) => setForm((s) => ({ ...s, lines: s.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const addPharmacy = (p) => setOpts((s) => ({ ...s, pharmacies: [...s.pharmacies, p].sort((a, b) => a.name.localeCompare(b.name)) }));
  const defaultPharmacy = opts.pharmacies.find((p) => p.id === form.pharmacy_id);

  useSaveShortcut(() => { if (!busy) save(false); }, open);
  const save = async (thenSign) => {
    setBusy(true);
    try {
      const lines = [];
      for (const l of form.lines) lines.push(await resolveLibrary(l));
      const body = { ...form, lines };
      const r = rx ? await api.put(`/prescriptions/${rx.id}`, body) : await api.post(`/clients/${client.id}/prescriptions`, body);
      toast.success(rx ? "Draft updated" : `Draft ${r.data.rx_number} saved`);
      onSaved(r.data, thenSign);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <SlidePanel open={open} onClose={onClose} width="max-w-3xl" testId="rx-editor" title={rx ? `Edit draft ${rx.rx_number}` : "New prescription"}
      subtitle={`${client.first_name} ${client.last_name}. Drafts stay editable until the prescriber signs.`}>
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div><FLabel>Prescriber *</FLabel><FSelect data-testid="rx-prescriber" value={form.prescriber_id} onChange={(e) => setForm({ ...form, prescriber_id: e.target.value })}>
            {meta.prescribers.map((p) => <option key={p.id} value={p.id}>{p.name}{p.credentials ? `, ${p.credentials}` : ""}</option>)}</FSelect></div>
          <div><FLabel>Written date *</FLabel><DateField testId="rx-written-date" value={form.written_date} noFuture onChange={(v) => setForm({ ...form, written_date: v })} /></div>
          <div><FLabel>Visit</FLabel><FSelect data-testid="rx-visit" value={form.appointment_id} placeholder={opts.visits.length ? "Not linked to a visit" : "No recent visits"} onChange={(e) => setForm({ ...form, appointment_id: e.target.value })}>
            {opts.visits.map((v) => <option key={v.id} value={v.id}>{fmtDate(v.date)} {v.start_time} · {v.type || "Visit"}</option>)}</FSelect></div>
          <div><FLabel>Pharmacy for all medications</FLabel><PharmacyPicker testId="rx-pharmacy" pharmacies={opts.pharmacies} value={form.pharmacy_id} placeholder="Link later"
            onPick={(id) => setForm((s) => ({ ...s, pharmacy_id: id }))} onAdd={addPharmacy} showContact /></div>
        </div>
        <p className="-mt-2 text-[11px] text-[#7B6157]">Each medication uses this pharmacy unless you pick a different one on that medication.{prefFilled && form.pharmacy_id && <span data-testid="rx-pharmacy-preferred-note"> Filled in from the client's preferred pharmacy.</span>}</p>
        {form.lines.map((l, i) => (
          <RxLineFields key={l.id || i} index={i} line={l} diagnoses={opts.diagnoses} canRemove={form.lines.length > 1} pharmacies={opts.pharmacies}
            onPharmacyAdded={addPharmacy} defaultPharmacy={defaultPharmacy}
            onChange={(p) => setLine(i, p)} onRemove={() => setForm((s) => ({ ...s, lines: s.lines.filter((_, j) => j !== i) }))} />
        ))}
        {form.lines.length < 10 && <button type="button" data-testid="rx-add-line" onClick={() => setForm((s) => ({ ...s, lines: [...s.lines, NEW_LINE()] }))}
          className="flex items-center gap-1.5 text-sm font-semibold text-[#4E342E] hover:underline"><Plus size={14} /> Add another medication</button>}
        <div><FLabel>Notes for the pharmacy</FLabel><FTextarea data-testid="rx-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" /></div>
        <div className="sticky bottom-0 -mx-6 flex justify-end gap-2 border-t border-[#F1EEE7] bg-white py-3 pl-6 pr-20">
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn variant="outline" type="button" data-testid="rx-save-draft" disabled={busy} onClick={() => save(false)} title={`Save draft (${SAVE_HINT})`} aria-keyshortcuts="Control+S"><Save size={14} /> Save draft</Btn>
          {form.prescriber_id === me.id && <Btn type="button" data-testid="rx-save-and-sign" disabled={busy} onClick={() => save(true)}><PenLine size={14} /> Save and review to sign</Btn>}
        </div>
      </div>
    </SlidePanel>
  );
}
